import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DollarSign, Building2, MapPin } from "lucide-react";
import StatCard from "@/components/StatCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BillingDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/dashboard/billing`)
      .then((res) => setData(res.data ?? {}))
      .catch((e) => {
        setError(e.response?.data?.detail || "Failed to load billing data");
      })
      .finally(() => setLoading(false));
  }, []);

  const rows = data?.rows ?? data?.items ?? data?.billing ?? [];
  const total = data?.total ?? data?.total_amount ?? data?.count ?? rows.length;

  return (
    <div className="space-y-6" data-testid="billing-dashboard-page">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard title="Total Records" value={total} icon={DollarSign} glowClass="stat-glow-green" />
        <StatCard title="Hospitals" value={data?.hospitals ?? data?.hospital_count ?? "—"} icon={Building2} glowClass="stat-glow-indigo" />
        <StatCard title="Territories" value={data?.territories ?? data?.territory_count ?? "—"} icon={MapPin} glowClass="stat-glow-amber" />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Billing Overview</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
          ) : error ? (
            <p className="text-sm text-destructive py-8 text-center">{error}</p>
          ) : Array.isArray(rows) && rows.length > 0 ? (
            <div className="overflow-auto h-[calc(100vh-320px)]">
              <Table className="min-w-[640px]">
                <TableHeader>
                  <TableRow>
                    {Object.keys(rows[0] ?? {}).slice(0, 6).map((key) => (
                      <TableHead key={key} className="capitalize">
                        {key.replace(/_/g, " ")}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, i) => (
                    <TableRow key={row.id ?? i}>
                      {Object.keys(rows[0] ?? {}).slice(0, 6).map((key) => (
                        <TableCell key={key} className="text-sm">
                          {typeof row[key] === "boolean" ? (
                            <Badge variant="outline" className="text-[10px]">
                              {row[key] ? "Yes" : "No"}
                            </Badge>
                          ) : (
                            String(row[key] ?? "—")
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">No billing records found</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
